import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { mergeWith } from 'lodash';
import { ignoreFalsyValues, isDummyUserPII, selectPreferredIdDocument } from 'utils/utilities';
import { useLazyGetTestResultQuery, useUpdateUserProfileMutation } from 'api/profile';
import { sessionUserProfileSaved } from 'features/onboarding/state/slice';
import { toDisplayDob } from 'lib/date';
import {
    selectCompany,
    selectMandatoryFields,
    selectSource,
    selectUserDetails,
    selectTestResult,
} from 'store/common/selectors/profile';
import { validationSchema, formValuesToAPI, handleError } from '../lib';
import { userDetailsFormInitialValues } from 'features/common/components/UserDetails/helper';
import { ROUTES } from 'utils/constants/routes';

// Ported verbatim from the former withFormik mapPropsToValues.
const buildInitialValues = ({
    user,
    result,
    source,
    isInformationMismatched,
    askForFallback,
    jwtToken,
    customVerificationOptions,
}) => {
    const userObjectData = user || {};

    // Pick identity fields from the document whose Result is complete (falling
    // back to the most recently uploaded one), never from a failed document
    // while the other side is usable.
    const resultData = selectPreferredIdDocument(result?.verifyUI);

    const pickValue = (resultValue, userValue) =>
        isInformationMismatched ? resultValue : userValue;

    const dateOfBirth = pickValue(resultData.dob, userObjectData?.date_of_birth);

    // Map snake-cased profile user_details onto the camelCase form fields.
    const formUser = {
        firstName: pickValue(resultData.firstName, userObjectData?.first_name),
        middleName: pickValue(resultData?.middleName, userObjectData?.middle_name),
        lastName: pickValue(resultData.lastName, userObjectData?.last_name),
        dob: toDisplayDob(dateOfBirth),
        email: userObjectData?.email,
        phone: userObjectData?.phone,
        address: userObjectData?.address,
        houseNumber: userObjectData?.house_number,
        streetName: userObjectData?.street_name,
        apartmentNumber: userObjectData?.apartment_number,
        city: userObjectData?.city,
        state: userObjectData?.state,
        zipCode: userObjectData?.zip_code,
        country: userObjectData?.country,
    };

    const isDummyUser = isDummyUserPII(formUser);

    const emptyFields = {
        ...formUser,
        firstName: isDummyUser ? '' : formUser.firstName,
        middleName: isDummyUser ? '' : formUser.middleName,
        lastName: isDummyUser ? '' : formUser.lastName,
        dob: isDummyUser ? '' : formUser.dob,
        email: isDummyUser && !userObjectData?.email_verified ? '' : formUser.email,
    };

    const values = mergeWith(
        {},
        userDetailsFormInitialValues,
        source === 'api' ? emptyFields : formUser,
        (a, b) => (b === null ? a : b)
    );
    values.token = jwtToken;
    values.customVerificationOptions = customVerificationOptions;
    values.acceptMiddleName = (isInformationMismatched || askForFallback) && !values.middleName;

    return values;
};

/**
 * Replaces the former connect()+withFormik container for UserDetails.
 * Reads the same Redux state the container mapped and builds the Formik bag.
 * Returns the spread formik fields (so the component's existing props.values /
 * props.setFieldValue / etc. keep working) plus the formik object (for
 * FormikProvider) and the Redux-derived values.
 *
 * @param {object} ownProps - props passed by the consumer (parent)
 */
export function useUserDetailsForm(ownProps) {
    const dispatch = useDispatch();
    const [triggerGetTestResult] = useLazyGetTestResultQuery();
    const [updateUserProfile] = useUpdateUserProfileMutation();

    const company = useSelector(selectCompany);
    const userDetails = useSelector(selectUserDetails);
    const mandatoryFields = useSelector(selectMandatoryFields);
    const result = useSelector(selectTestResult);
    const source = useSelector(selectSource);

    const user = userDetails;
    const jwtToken = company?.jwt;
    const customVerificationOptions = useMemo(
        () =>
            company?.userInformationStatus
                ? { userInformationStatus: company.userInformationStatus }
                : null,
        [company?.userInformationStatus]
    );

    const { isInformationMismatched, askForFallback, callback } = ownProps;

    const location = useLocation();
    const isRiskAssessmentRoute = location?.pathname === ROUTES.RISK_ASSESSMENT;

    const computedInitialValues = useMemo(
        () =>
            buildInitialValues({
                user,
                result,
                source,
                isInformationMismatched,
                askForFallback,
                jwtToken,
                customVerificationOptions,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            user,
            result,
            source,
            isInformationMismatched,
            askForFallback,
            jwtToken,
            customVerificationOptions,
        ]
    );

    const formik = useFormik({
        initialValues: computedInitialValues,
        enableReinitialize: true,
        validationSchema: validationSchema({
            ...ownProps,
            userDetails,
            mandatoryFields,
            company,
            isRiskAssessmentRoute,
        }),

        onSubmit: (values, { setStatus, setSubmitting }) => {
            values.customVerificationOptions = {
                ...values.customVerificationOptions,
                userInformationStatus: 'confirmedViaUser',
            };

            const submitValues = source === 'api' ? values : ignoreFalsyValues(values);

            updateUserProfile(formValuesToAPI(submitValues))
                .unwrap()
                .then(async () => {
                    await dispatch(sessionUserProfileSaved());
                    setSubmitting(false);
                    await triggerGetTestResult(user.uuid);
                    setStatus({ status: 'COMPLETED' });
                    if (callback) {
                        await callback();
                    }
                })
                .catch((error) => {
                    handleError({ response: { data: error?.data } });
                    setSubmitting(false);
                });
        },
    });

    return { ...formik, formik, user, source, userDetails, mandatoryFields, company };
}
